import { deriveApplicationsRoute } from "./applications.js";
import { resolvePortalRedirect } from "./portal-redirects.js";

/**
 * Whether a path is owned by the Specialists (agents) view.
 *
 * `App.tsx`'s `RoutedPage` matches the known top-level routes with explicit
 * branches and used to fall through to the Specialists list for *anything*
 * else — so a mistyped or dead URL silently rendered Specialists with the bogus
 * path still in the address bar. This predicate is the boundary that lets the
 * router send only genuine Specialists paths to `AgentsPage` and route every
 * other unmatched path to the Not Found page instead.
 */
export function isSpecialistsPath(pathname: string): boolean {
  return pathname === "/specialists" || pathname.startsWith("/specialists/");
}

/**
 * Whether a path is a well-formed Workspace route. Unlike Specialists (which
 * owns arbitrarily deep sub-paths), Workspace accepts exactly `/workspace`,
 * `/workspace/:agentId`, and `/workspace/:agentId/:sessionId` — a deeper path
 * is malformed and resolves to Not Found so a stray segment never renders the
 * page against a bogus URL.
 */
export function isWorkspacePath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "workspace" || segments.length > 3) return false;
  // A malformed percent-encoded segment (e.g. `/workspace/%E0`) names no real
  // agent or session; reject the whole path so `derivePortalPage` routes it to
  // not-found rather than letting `deriveWorkspaceRoute` throw at render.
  if (segments.some((segment) => decodeSegment(segment) === undefined)) return false;
  // `new` and `conversations` address the whole surface rather than an agent, so
  // they take no further segment; a trailing one is malformed and resolves to
  // Not Found, keeping the rendered page and its breadcrumb in agreement.
  const first = decodeSegment(segments[1]);
  return !(
    (first === WORKSPACE_NEW_SEGMENT ||
      first === WORKSPACE_CONVERSATIONS_SEGMENT ||
      first === WORKSPACE_ARCHIVED_SEGMENT) &&
    segments.length > 2
  );
}

/**
 * Decode one path segment, returning undefined when it is absent or its
 * percent-encoding is malformed (`decodeURIComponent` throws). Callers treat
 * undefined as "no such segment", keeping a malformed Workspace URL out of the
 * router instead of crashing the render.
 */
function decodeSegment(segment: string | undefined): string | undefined {
  if (segment === undefined) return undefined;
  try {
    return decodeURIComponent(segment);
  } catch {
    return undefined;
  }
}

/**
 * The reserved first segment that asks for a new conversation. Bare
 * `/workspace` opens the newest existing conversation, so an explicit address is
 * the only way to reach the entry composer once the account has history. Mirrors
 * the existing `/specialists/new` reservation, and shadows an agent whose id is
 * literally `new`.
 */
export const WORKSPACE_NEW_SEGMENT = "new";

/**
 * The reserved first segment that asks for the full conversation list. Bare
 * `/workspace` opens the newest existing conversation, so this explicit address
 * is the only way a mobile viewer inside a thread can reach the rail again.
 * Shadows an agent whose id is literally `conversations`, the same tradeoff as
 * `new`.
 */
export const WORKSPACE_CONVERSATIONS_SEGMENT = "conversations";

/**
 * The reserved first segment that asks for the archived-conversation recovery
 * list. Its own address rather than a mode of the rail: recovering a
 * conversation is rare and deliberate, and the rail is what archiving is for.
 * Shadows an agent whose id is literally `archived`, the same tradeoff as
 * `new` and `conversations`.
 */
export const WORKSPACE_ARCHIVED_SEGMENT = "archived";

/**
 * The conversation list addressed with a text filter already applied. The rail
 * answers `q` against message text server-side, so this is the address the
 * global search hands a conversation query off to rather than indexing
 * transcripts itself.
 */
export function workspaceConversationSearchPath(query: string): string {
  return `/workspace/${WORKSPACE_CONVERSATIONS_SEGMENT}?q=${encodeURIComponent(query)}`;
}

/**
 * Parse the Workspace identity carried in the URL. Every part is optional:
 * `/workspace` selects no conversation (the page opens the newest one),
 * `/workspace/new` asks for the entry composer instead,
 * `/workspace/conversations` asks for the full conversation list,
 * `/workspace/archived` asks for the archived-conversation recovery list,
 * `/workspace/:agentId` targets an agent (the page mints a fresh session), and
 * the third segment names the session.
 */
export function deriveWorkspaceRoute(pathname: string): {
  agentId?: string;
  sessionId?: string;
  startNew?: boolean;
  showList?: boolean;
  showArchived?: boolean;
} {
  const segments = pathname.replace(/^\//, "").split("/").filter(Boolean);
  const first = decodeSegment(segments[1])?.trim();
  if (first === WORKSPACE_NEW_SEGMENT) return { startNew: true };
  if (first === WORKSPACE_CONVERSATIONS_SEGMENT) return { showList: true };
  if (first === WORKSPACE_ARCHIVED_SEGMENT) return { showArchived: true };
  const sessionId = decodeSegment(segments[2])?.trim();
  return {
    ...(first ? { agentId: first } : {}),
    ...(sessionId ? { sessionId } : {}),
  };
}

/**
 * The content page module owned by a portal pathname. Keeping this whitelist
 * beside `isSpecialistsPath` gives the SPA router and its tests one shared
 * registry: a retired standalone route cannot keep rendering through a
 * forgotten branch in `App.tsx`. The cross-package OAuth callback contract
 * remains an explicit special case in `App.tsx`.
 */
export type PortalPageId =
  | "dashboard"
  | "approvals"
  | "usage"
  | "dashboards"
  | "applications"
  | "automations"
  | "account"
  | "settings"
  | "security"
  | "channel-admin"
  | "provider-settings"
  | "connector-admin"
  | "agent-teams"
  | "people-and-roles"
  | "org-api-keys"
  | "archived-specialists"
  | "skill-editor"
  | "skills"
  | "specialists"
  | "workspace"
  | "not-found";

export type PortalShellClass = "page" | "workbench";

export function derivePortalPage(pathname: string): PortalPageId {
  // Any path `portal-redirects` forwards resolves to not-found, so `App.tsx`
  // shows the neutral RouteFallback for the one frame before its redirect effect
  // rewrites the URL — never the retired page.
  if (resolvePortalRedirect(pathname)) return "not-found";
  if (pathname === "/" || pathname === "") return "dashboard";
  if (
    pathname === "/approvals" ||
    pathname === "/approvals/awaiting" ||
    pathname === "/approvals/decided" ||
    /^\/approvals\/parcel\/[^/]+$/.test(pathname)
  ) {
    return "approvals";
  }
  if (pathname === "/usage") return "usage";
  if (isDashboardsPath(pathname)) return "dashboards";
  if (isApplicationsPath(pathname)) return "applications";
  if (isAutomationsPath(pathname)) return "automations";
  if (pathname === "/account") return "account";
  if (pathname === "/settings/providers" || pathname === "/settings/providers/")
    return "provider-settings";
  if (pathname === "/settings/security" || pathname === "/settings/security/") return "security";
  if (pathname === "/settings/connectors" || pathname === "/settings/connectors/") {
    return "connector-admin";
  }
  if (pathname === "/settings/channels" || pathname === "/settings/channels/") {
    return "channel-admin";
  }
  if (pathname === "/settings/agent-teams" || pathname === "/settings/agent-teams/") {
    return "agent-teams";
  }
  if (isPeopleAndRolesPath(pathname)) {
    return "people-and-roles";
  }
  if (pathname === "/settings/api-keys" || pathname === "/settings/api-keys/") {
    return "org-api-keys";
  }
  if (pathname === "/settings/archived" || pathname === "/settings/archived/") {
    return "archived-specialists";
  }
  if (pathname === "/settings" || pathname === "/settings/") return "settings";
  if (pathname.startsWith("/skills")) {
    if (pathname === "/skills" || pathname === "/skills/") return "skills";
    return /^\/skills\/[^/]+$/.test(pathname) ? "skill-editor" : "not-found";
  }
  if (isSpecialistsPath(pathname)) return "specialists";
  if (pathname === "/workspace" || pathname.startsWith("/workspace/")) {
    return isWorkspacePath(pathname) ? "workspace" : "not-found";
  }
  return "not-found";
}

/**
 * Whether a path is an Automations route the shell owns: the list, one
 * automation, and one of its runs. An automation has no chromeless renderer —
 * there is nothing to view, only a record of what ran.
 */
function isAutomationsPath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "automations") return false;
  if (segments.length === 1) return true;
  if (decodeSegment(segments[1]) === undefined) return false;
  if (segments.length === 2) return true;
  return (
    segments.length === 4 && segments[2] === "runs" && decodeSegment(segments[3]) !== undefined
  );
}

/**
 * Whether a path is a Dashboards route the shell owns. A dashboard itself
 * renders chromeless (`/view/dashboard/:id`), so the shell owns only the
 * catalogue.
 */
function isDashboardsPath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 1 && segments[0] === "dashboards";
}

/**
 * Whether a path is an Applications route the shell owns: the list, blueprints,
 * and an external application (which has no chromeless renderer). An internal
 * application itself renders chromeless (`/view/application/:id`).
 */
function isApplicationsPath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "applications") return false;
  if (segments.length === 1) return true;
  if (segments[1] === "blueprints") {
    return (
      segments.length === 2 || (segments.length === 3 && decodeSegment(segments[2]) !== undefined)
    );
  }
  return (
    segments.length === 3 && deriveApplicationsRoute(`/${segments.join("/")}`).kind === "external"
  );
}

function isPeopleAndRolesPath(pathname: string): boolean {
  if (pathname === "/settings/people-and-roles" || pathname === "/settings/people-and-roles/") {
    return true;
  }
  const match = pathname.match(/^\/settings\/people-and-roles\/([^/]+)$/);
  return match ? decodeSegment(match[1]) !== undefined : false;
}

export function derivePortalShellClass(pathname: string): PortalShellClass {
  const page = derivePortalPage(pathname);
  return page === "workspace" ? "workbench" : "page";
}
